import { Link } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { apps } from '../data/apps';
import { setPageMeta } from '../utils/setPageMeta';

function useScrollAnimation(threshold = 0.1) {
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold }
    );
    const currentRef = ref.current;
    if (currentRef) observer.observe(currentRef);
    return () => { if (currentRef) observer.unobserve(currentRef); };
  }, [threshold]);

  return { ref, isVisible };
}

function CustomCursor() {
  const [position, setPosition] = useState({ x: -100, y: -100 });
  const [isHovering, setIsHovering] = useState(false);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      setPosition({ x: e.clientX, y: e.clientY });
      const target = e.target as HTMLElement;
      setIsHovering(!!target.closest('a, button'));
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <>
      <div
        className={`custom-cursor ${isHovering ? 'hovering' : ''}`}
        style={{ left: `${position.x}px`, top: `${position.y}px` }}
      />
      <div
        className={`custom-cursor-dot ${isHovering ? 'hovering' : ''}`}
        style={{ left: `${position.x}px`, top: `${position.y}px` }}
      />
    </>
  );
}

export default function Apps() {
  const [scrollY, setScrollY] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const appsSection = useScrollAnimation(0.1);

  useEffect(() => {
    setPageMeta({
      title: 'Our Apps | LeadingNG — Visual Motivator, Reap & more',
      description:
        'LeadingNG mobile apps: Visual Motivator for goals and daily quotes, and links to products like Reap for investor relations. Built with React Native, Expo and modern cloud backends.',
      canonicalPath: '/app',
      keywords:
        'LeadingNG apps, Visual Motivator app, Reap app, React Native, Expo, mobile software, investor relations app',
    });
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight - windowHeight;
      setScrollProgress(documentHeight > 0 ? (window.scrollY / documentHeight) * 100 : 0);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <>
      <CustomCursor />
      <div className="scroll-progress-bar" style={{ width: `${scrollProgress}%` }} aria-hidden="true" />

      <section className="page-hero page-hero-apps">
        <div className="hero-bg" style={{ transform: `translateY(${scrollY * 0.5}px)` }} />
        <div className="container text-center">
          <span className="page-hero-label animate-fade-in">Apps</span>
          <h1 className="page-title animate-slide-up" style={{ animationDelay: '0.1s' }}>
            Our apps
          </h1>
          <p className="page-intro animate-fade-in" style={{ animationDelay: '0.3s' }}>
            Mobile apps we build and publish.
          </p>
        </div>
      </section>

      <section ref={appsSection.ref} className={`section apps-section ${appsSection.isVisible ? 'visible' : ''}`}>
        <div className="apps-wrapper">
          <div className="apps-count scroll-reveal">
            <p><strong>{apps.length}</strong> {apps.length === 1 ? 'app' : 'apps'}</p>
          </div>
          <div className="apps-grid">
            {apps.map((app, i) => {
              const cardContent = (
                <>
                  {app.icon && (
                    <img
                      src={`${process.env.PUBLIC_URL || ''}/${app.icon}`}
                      alt=""
                      className="app-card-icon"
                      width={64}
                      height={64}
                      loading="lazy"
                    />
                  )}
                  <div className="app-card-content">
                    <h3 className="app-card-title">{app.name}</h3>
                    <span className="app-card-link">{app.tagline} →</span>
                    <p className="app-card-desc">{app.description}</p>
                  </div>
                </>
              );
              const cardClass = `app-card scroll-reveal`;
              const style = { transitionDelay: `${0.2 + i * 0.05}s` };
              return app.url ? (
                <a
                  key={app.slug}
                  href={app.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={cardClass}
                  style={style}
                >
                  {cardContent}
                </a>
              ) : (
                <Link key={app.slug} to={`/app/${app.slug}`} className={cardClass} style={style}>
                  {cardContent}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      <style>{`
        .custom-cursor, .custom-cursor-dot {
          position: fixed; pointer-events: none; z-index: 10000; transform: translate(-50%, -50%);
        }
        .custom-cursor {
          width: 40px; height: 40px; border: 2px solid var(--color-primary); border-radius: 50%;
          transition: width 0.3s ease, height 0.3s ease, border-color 0.3s ease; opacity: 0.5;
        }
        .custom-cursor.hovering {
          width: 60px; height: 60px; border-color: var(--color-secondary); opacity: 0.8;
        }
        .custom-cursor-dot {
          width: 6px; height: 6px; background: var(--color-primary); border-radius: 50%;
          transition: transform 0.15s ease, background 0.3s ease;
        }
        .custom-cursor-dot.hovering {
          transform: translate(-50%, -50%) scale(1.5); background: var(--color-secondary);
        }
        .scroll-progress-bar {
          position: fixed; top: 0; left: 0; height: 3px;
          background: linear-gradient(90deg, var(--color-primary), var(--color-secondary));
          z-index: 9999; transition: width 0.1s ease-out; box-shadow: 0 0 10px var(--color-primary);
        }
        .animate-fade-in { opacity: 0; animation: appsFadeIn 1s ease-out forwards; }
        .animate-slide-up { opacity: 0; transform: translateY(30px); animation: appsSlideUp 0.8s ease-out forwards; }
        @keyframes appsFadeIn { to { opacity: 1; } }
        @keyframes appsSlideUp { to { opacity: 1; transform: translateY(0); } }
        .scroll-reveal {
          opacity: 0; transform: translateY(40px); transition: opacity 0.8s ease, transform 0.8s ease;
        }
        .visible .scroll-reveal { opacity: 1; transform: translateY(0); }

        .page-hero-apps {
          position: relative; padding: 88px 20px 64px; overflow: hidden; color: #fff;
        }
        .page-hero-apps .hero-bg {
          position: absolute; inset: 0;
          background: linear-gradient(160deg, var(--color-bg-dark) 0%, #1a252f 100%);
          z-index: 0;
        }
        .page-hero-apps > .container { position: relative; z-index: 1; }
        .page-hero-apps .page-hero-label {
          display: block; font-size: 0.75rem; font-weight: 700; text-transform: uppercase;
          letter-spacing: 0.12em; color: var(--color-primary); margin-bottom: 8px;
        }
        .page-hero-apps .page-title { margin: 0 0 12px; font-size: 2rem; font-weight: 700; letter-spacing: -0.02em; }
        .page-hero-apps .page-intro { margin: 0; opacity: 0.92; font-size: 1.05rem; }

        .apps-section {
          background: linear-gradient(180deg, #f5f6f8 0%, #e8eaed 100%);
          padding-top: 48px; padding-bottom: 64px; position: relative;
        }
        .apps-wrapper { max-width: 1200px; margin: 0 auto; padding: 0 20px; }
        .apps-count { text-align: center; margin-bottom: 24px; }
        .apps-count p { font-size: 0.95rem; color: var(--color-text-muted); margin: 0; }
        .apps-count strong { color: var(--color-primary); font-weight: 600; }

        .apps-grid {
          display: grid; grid-template-columns: repeat(2, 1fr); gap: 24px;
        }
        .app-card {
          background: #fff; border: 1px solid var(--color-border); border-radius: var(--radius);
          padding: 28px; transition: box-shadow 0.3s ease, transform 0.3s ease, border-color 0.3s ease;
          box-shadow: var(--shadow-sm); position: relative; overflow: hidden;
          display: flex; flex-direction: column; text-decoration: none; color: inherit;
        }
        .app-card-icon {
          width: 64px; height: 64px; object-fit: contain; margin-bottom: 16px;
          border-radius: var(--radius-sm); flex-shrink: 0;
        }
        .app-card::before {
          content: ''; position: absolute; top: 0; left: 0; width: 4px; height: 100%;
          background: linear-gradient(180deg, var(--color-primary), var(--color-secondary));
          transform: scaleY(0); transition: transform 0.3s ease; transform-origin: top;
        }
        .app-card:hover::before { transform: scaleY(1); }
        .app-card:hover {
          box-shadow: var(--shadow-md); transform: translateY(-4px); border-color: var(--color-primary);
        }
        .app-card-content { display: flex; flex-direction: column; flex: 1; }
        .app-card-title {
          font-size: 1.15rem; font-weight: 700; color: var(--color-bg-dark); margin: 0 0 12px;
          transition: color 0.3s ease; line-height: 1.3;
        }
        .app-card:hover .app-card-title { color: var(--color-primary); }
        .app-card-link {
          display: inline-block; font-size: 0.9rem; color: var(--color-link); margin-bottom: 12px;
          font-weight: 500; transition: color 0.3s ease;
        }
        .app-card:hover .app-card-link { color: var(--color-primary); }
        .app-card-desc {
          font-size: 0.95rem; color: var(--color-text-muted); line-height: 1.6; margin: 0;
          flex: 1;
        }

        @media (prefers-reduced-motion: reduce) {
          .animate-fade-in, .animate-slide-up, .scroll-reveal { animation: none !important; opacity: 1 !important; transform: none !important; }
          .custom-cursor, .custom-cursor-dot { display: none; }
          .scroll-progress-bar { display: none; }
        }
        @media (max-width: 768px) {
          .custom-cursor, .custom-cursor-dot { display: none; }
          .scroll-progress-bar { height: 2px; }
          .apps-grid { grid-template-columns: 1fr; gap: 20px; }
          .app-card { padding: 24px; }
        }
        @media (max-width: 480px) {
          .app-card { padding: 20px; }
          .app-card-title { font-size: 1.05rem; }
          .app-card-desc { font-size: 0.9rem; }
        }
      `}</style>
    </>
  );
}
